import { HTTPService } from "./httpService";

const API = new HTTPService();

// Helper functions for computing holiday dates

// Get nth weekday of a month (1-indexed)
const getNthWeekday = (year, month, weekday, n) => {
  const firstDay = new Date(year, month - 1, 1);
  const firstWeekday = firstDay.getDay();
  let diff = weekday - firstWeekday;
  if (diff < 0) diff += 7;
  const day = 1 + diff + (n - 1) * 7;
  const date = new Date(year, month - 1, day);
  return `${year}-${String(month).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Get last Monday of a month
const getLastMonday = (year, month) => {
  const lastDay = new Date(year, month, 0); // Last day of the month
  const lastDayOfWeek = lastDay.getDay();
  // Calculate days to subtract to get to last Monday (0=Sun, 1=Mon, ...)
  let diff = lastDayOfWeek - 1;
  if (diff < 0) diff += 7;
  const day = lastDay.getDate() - diff;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// Get first Monday of a month
const getFirstMonday = (year, month) => {
  return getNthWeekday(year, month, 1, 1); // weekday 1 = Monday
};

// Get second Monday of a month
const getSecondMonday = (year, month) => {
  return getNthWeekday(year, month, 1, 2);
};

// Get third Monday of a month
const getThirdMonday = (year, month) => {
  return getNthWeekday(year, month, 1, 3);
};

// Get fourth Thursday of a month (for Thanksgiving)
const getFourthThursday = (year, month) => {
  return getNthWeekday(year, month, 4, 4); // weekday 4 = Thursday
};

// Preset US Holidays (computed each year)
export const US_HOLIDAYS = [
  { id: 'new_years', name: "New Year's Day", getDate: (year) => `${year}-01-01` },
  { id: 'mlk', name: 'Martin Luther King Jr. Day', getDate: (year) => getThirdMonday(year, 1) },
  { id: 'presidents', name: "Presidents' Day", getDate: (year) => getThirdMonday(year, 2) },
  { id: 'memorial', name: 'Memorial Day', getDate: (year) => getLastMonday(year, 5) },
  { id: 'juneteenth', name: 'Juneteenth', getDate: (year) => `${year}-06-19` },
  { id: 'independence', name: 'Independence Day', getDate: (year) => `${year}-07-04` },
  { id: 'labor', name: 'Labor Day', getDate: (year) => getFirstMonday(year, 9) },
  { id: 'columbus', name: 'Columbus Day', getDate: (year) => getSecondMonday(year, 10) },
  { id: 'veterans', name: 'Veterans Day', getDate: (year) => `${year}-11-11` },
  { id: 'thanksgiving', name: 'Thanksgiving', getDate: (year) => getFourthThursday(year, 11) },
  { id: 'christmas_eve', name: 'Christmas Eve', getDate: (year) => `${year}-12-24` },
  { id: 'christmas', name: 'Christmas Day', getDate: (year) => `${year}-12-25` },
  { id: 'new_years_eve', name: "New Year's Eve", getDate: (year) => `${year}-12-31` },
];

// Get default operating hours (Mon-Fri 8-5, Sat-Sun closed)
export const getDefaultOperatingHours = () => {
  return [
    { day_of_week: 0, day_name: 'Sunday', is_open: false, open_time: null, close_time: null },
    { day_of_week: 1, day_name: 'Monday', is_open: true, open_time: '08:00', close_time: '17:00' },
    { day_of_week: 2, day_name: 'Tuesday', is_open: true, open_time: '08:00', close_time: '17:00' },
    { day_of_week: 3, day_name: 'Wednesday', is_open: true, open_time: '08:00', close_time: '17:00' },
    { day_of_week: 4, day_name: 'Thursday', is_open: true, open_time: '08:00', close_time: '17:00' },
    { day_of_week: 5, day_name: 'Friday', is_open: true, open_time: '08:00', close_time: '17:00' },
    { day_of_week: 6, day_name: 'Saturday', is_open: false, open_time: null, close_time: null },
  ];
};

// Format time from 24hr to 12hr format
export const formatTime = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Format date for display
export const formatHolidayDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const LocationService = {
  // Operating Hours
  getOperatingHours: async (locationId) => {
    try {
      const { data } = await API.get(`/location/${locationId}/hours`);
      return data;
    } catch (error) {
      // If no hours set yet, return defaults
      if (error.response?.status === 404) {
        return { hours: getDefaultOperatingHours() };
      }
      throw error;
    }
  },

  saveOperatingHours: async (locationId, hours) => {
    const { data } = await API.put(`/location/${locationId}/hours`, { hours });
    return data;
  },

  // Blocked Dates
  getBlockedDates: async (locationId) => {
    try {
      const { data } = await API.get(`/location/${locationId}/blocked-dates`);
      return data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { blockedDates: [] };
      }
      throw error;
    }
  },

  // For holidays and custom closures, send array of dates with reason
  addBlockedDates: async (locationId, blocks) => {
    // blocks: [{ date: 'YYYY-MM-DD', reason: 'Christmas' }, ...]
    const { data } = await API.post(`/location/${locationId}/blocked-dates`, { blocks });
    return data;
  },

  removeBlockedDate: async (locationId, date) => {
    await API.delete(`/location/${locationId}/blocked-dates/${date}`);
  },

  // Convenience method to toggle a holiday
  toggleHoliday: async (locationId, date, holidayName, isCurrentlyBlocked) => {
    if (isCurrentlyBlocked) {
      await LocationService.removeBlockedDate(locationId, date);
    } else {
      await LocationService.addBlockedDates(locationId, [{ date, reason: holidayName }]);
    }
  },

  // Add date range (expands range into individual dates)
  addDateRange: async (locationId, startDate, endDate, reason) => {
    const blocks = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      blocks.push({ date: dateStr, reason });
      current.setDate(current.getDate() + 1);
    }

    if (blocks.length > 0) {
      return await LocationService.addBlockedDates(locationId, blocks);
    }
    return { blockedDates: [] };
  },

  // Remove a date range
  removeDateRange: async (locationId, startDate, endDate) => {
    const current = new Date(startDate);
    const end = new Date(endDate);
    const promises = [];

    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      promises.push(LocationService.removeBlockedDate(locationId, dateStr).catch(() => {}));
      current.setDate(current.getDate() + 1);
    }

    await Promise.all(promises);
  }
};

export default LocationService;
